import { useEffect, useMemo } from 'react'

import type { one_to_one } from '@fireworkhq/proto-contract'
import { useAppointment } from '@liveHelper/context/AppointmentContext/AppointmentContext'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { useIsActiveConversation } from '@liveHelper/hooks/useIsActiveConversation'
import { useShowWelcomeMessage } from '@liveHelper/hooks/useShowWelcomeMessage'
import { removeConversation as removePersistedConversation } from '@liveHelper/models/persistence/conversation'
import {
  actionsSelector,
  callDataSelector,
  conversationSelector,
  messagesSelector,
  paramsSelector,
  useStoreApi,
  visitorInformationSelector,
} from '@liveHelper/models/store'
import type {
  LiveHelperState,
  TerminateCallProps,
} from '@liveHelper/models/store/libs/actions'
import { customLiveHelperEvent } from '@liveHelper/tracking/customLiveHelperEvent'
import { EventType } from '@liveHelper/tracking/eventType'
import {
  useTrackAcceptIncomingCall,
  useTrackCallAgent,
  useTrackDeclineIncomingCall,
  useTrackJoinConversation,
  useTrackSendMessage,
  useTrackStopCallAgent,
} from '@liveHelper/tracking/useLiveHelperTracking'
import type {
  Message,
  MessageAttachmentUploadStatus,
} from '@oneToOne/shared/components/chat/types'
import { useDisconnectOpenSocket } from '@oneToOne/shared/contexts/useDisconnectOpenSocket'
import type { Appointment } from '@oneToOne/shared/services/rest/types'

import { uuidv4 } from '@core/helpers/uuidv4'
import { useContextTranslation } from '@core/i18n'
import { logError } from '@core/logging'
import { safeLogVariable } from '@core/logging/helpers'
import { promisifyPush } from '@core/phoenix'

import type { VisitorChannel } from './types'

type TranslationFn = ReturnType<typeof useContextTranslation>['t']

function shouldSendPrequalificationMessage(state: LiveHelperState) {
  if (state.conversation) {
    return false
  }
  const messages = messagesSelector(state)
  if (!messages.length || messages.slice(-1)[0]?.conversation_id) {
    return true
  }
  return false
}

async function joinConversation(
  channel: VisitorChannel,
  storeApi: ReturnType<typeof useStoreApi>,
  trackJoinConversation: ReturnType<typeof useTrackJoinConversation>,
  t: TranslationFn,
  isConversationIntentEnabled = false,
  appointment?: Appointment,
): Promise<one_to_one.shared.Conversation> {
  const actions = actionsSelector(storeApi.getState())
  const params = paramsSelector(storeApi.getState())
  const visitorInformation = visitorInformationSelector(storeApi.getState())

  return promisifyPush(
    channel.push('join_conversation', {
      name: visitorInformation?.firstName,
      email: visitorInformation?.email,
      appointment_id: appointment?.id,
    }),
  )
    .then(({ conversation }) => {
      if (conversation) {
        actions.setConversation(conversation)
        trackJoinConversation({ conversation, visitorInitiated: true })
        customLiveHelperEvent({
          type: EventType.FW_LIVE_HELPER_START_CONVERSATION,
          data: {
            widget_id: params?.widget_id,
          },
        })

        if (!isConversationIntentEnabled) {
          const messages: one_to_one.shared.Message[] = []
          messages.push({
            client_msg_ref: uuidv4(),
            created_at: new Date().toISOString(),
            system_message: {
              content: t('oneToOne:Connecting you to an agent'),
              type: 'SYSTEM_MESSAGE_TYPE_INFO',
            },
          })
          actions.addChatMessages(messages, 'visitor')
        }

        return conversation
      } else {
        throw new Error('No conversation returned')
      }
    })
    .catch((error: unknown) => {
      if (error instanceof Error) {
        logError(error)
      }
      throw error
    })
}

/**
 * Sets all actions that need access to the WebSocket connection for the visitor
 * agent WebSocket topic.
 */
export const useSetVisitorChannelActions = (
  channel: VisitorChannel | undefined,
): void => {
  const visitorActions = useVisitorActions(channel)
  const callActions = useCallActions(channel)
  const textChatActions = useTextChatActions(channel)

  const storeApi = useStoreApi()

  useEffect(() => {
    if (!channel) {
      return
    }

    const { setVisitorPrivateActions, removeVisitorPrivateActions } =
      actionsSelector(storeApi.getState())
    setVisitorPrivateActions({
      ...visitorActions,
      ...callActions,
      ...textChatActions,
    })

    return () => {
      removeVisitorPrivateActions()
    }
  }, [storeApi, channel, visitorActions, callActions, textChatActions])
}

function useVisitorActions(channel: VisitorChannel | undefined) {
  const storeApi = useStoreApi()
  const isActiveConversation = useIsActiveConversation()
  const disconnectOpenSocket = useDisconnectOpenSocket()

  return useMemo(() => {
    if (!channel) {
      return
    }

    const actions = actionsSelector(storeApi.getState())
    const params = paramsSelector(storeApi.getState())

    return {
      endConversation: () => {
        const conversation = conversationSelector(storeApi.getState())
        channel.push('terminate_conversation', {
          conversation_id: conversation?.id,
        })
        if (!(
          conversation?.intent === 'CONVERSATION_INTENT_POST_SALES' &&
          !conversation?.assistance_requested
        )) {
          if (
            params.dismiss_after_conversation_ended &&
            !isActiveConversation
          ) {
            actions.hideWidget()
            disconnectOpenSocket()
          } else {
            actions.sendUIStateTransitionEvent({
              type: 'END_CONVERSATION',
              isActiveConversation,
            })
          }
        }
        actions.removeConversation()
        removePersistedConversation()
      },
      updateActiveStatus: (isActive: boolean) => {
        channel.push('update_active_status', { is_active: isActive })
      },
      updatePrivacyConfiguration: (
        privacyConfiguration: one_to_one.enums.PrivacyConfiguration,
      ) => {
        channel.push('update_privacy_configuration', {
          privacy_configuration: privacyConfiguration,
        })
      },
    }
  }, [channel, storeApi, isActiveConversation, disconnectOpenSocket])
}

function useCallActions(channel: VisitorChannel | undefined) {
  const storeApi = useStoreApi()
  const trackCallAgent = useTrackCallAgent()
  const trackStopCallAgent = useTrackStopCallAgent()
  const trackAcceptIncomingCall = useTrackAcceptIncomingCall()
  const trackDeclineIncomingCall = useTrackDeclineIncomingCall()
  const trackJoinConversation = useTrackJoinConversation()
  const { t } = useContextTranslation()
  const chatChannelConfig = useChatChannelConfig()
  const disconnectOpenSocket = useDisconnectOpenSocket()
  const { appointment } = useAppointment()

  return useMemo(() => {
    if (!channel) {
      return {}
    }
    const {
      sendUIStateTransitionEvent,
      terminateCall,
      setClientRole,
      setCallId,
      removeConversation,
      hideWidget,
    } = actionsSelector(storeApi.getState())

    return {
      callAgent: async (
        { isTransferredConversation } = { isTransferredConversation: false },
      ) => {
        sendUIStateTransitionEvent({ type: 'DIAL', isTransferredConversation })

        if (callDataSelector(storeApi.getState())) {
          return
        }

        let conversation = conversationSelector(storeApi.getState())
        if (!conversation) {
          conversation = await joinConversation(
            channel,
            storeApi,
            trackJoinConversation,
            t,
            chatChannelConfig?.config?.classify_conversation_intent,
            appointment,
          )
        }
        channel
          .push('call_agent', {
            conversation_id: conversation.id,
          })
          .receive('ok', (response) => {
            const videoCall = response?.video_call
            setCallId(videoCall?.id ?? '', Boolean(appointment))
            trackCallAgent()
          })
          .receive('error', (error) => {
            logError(new Error(safeLogVariable(error)))
            terminateCall()
            sendUIStateTransitionEvent('TERMINATE_CALL')
          })
          .receive('timeout', () => {
            logError(new Error('Socket timeout on call_agent'))
            terminateCall()
            sendUIStateTransitionEvent('TERMINATE_CALL')
          })
      },
      takeCall: async (agentId?: string) => {
        const conversation = conversationSelector(storeApi.getState())
        try {
          if (conversation) {
            channel.push('take_call', {
              agent_id: agentId,
              conversation_id: conversation?.id,
            })
            setClientRole('host')
            sendUIStateTransitionEvent('START_CALL')
            trackAcceptIncomingCall()
          } else {
            throw new Error('Cannot take call without conversation')
          }
        } catch (error: unknown) {
          if (error instanceof Error) {
            logError(error)
          }
        }
      },
      terminateCall: async ({ agentId, type }: TerminateCallProps) => {
        if (type === 'stopDialing') {
          trackStopCallAgent()
        } else if (type === 'declineIncomingCall') {
          trackDeclineIncomingCall()
        }

        terminateCall()
        sendUIStateTransitionEvent('TERMINATE_CALL')

        let response
        const conversation = conversationSelector(storeApi.getState())
        try {
          response = await promisifyPush(
            channel.push('terminate_call', {
              agent_id: agentId,
              conversation_id: conversation?.id,
            }),
          )
        } catch (e) {
          // we should not throw any error when visitor is terminating the call
        } finally {
          const isActiveConversation = Boolean(response?.is_conversation_active)
          if (!isActiveConversation && !appointment) {
            removeConversation()
            removePersistedConversation()
            sendUIStateTransitionEvent({
              type: 'END_CONVERSATION',
              isActiveConversation,
            })
            const params = paramsSelector(storeApi.getState())
            if (params.dismiss_after_conversation_ended) {
              hideWidget()
              disconnectOpenSocket()
            }
          }
        }
      },
    }
  }, [
    channel,
    storeApi,
    trackJoinConversation,
    t,
    chatChannelConfig?.config?.classify_conversation_intent,
    trackCallAgent,
    trackAcceptIncomingCall,
    trackStopCallAgent,
    trackDeclineIncomingCall,
    disconnectOpenSocket,
    appointment,
  ])
}

function useTextChatActions(channel: VisitorChannel | undefined) {
  const storeApi = useStoreApi()
  const { t } = useContextTranslation()
  const trackJoinConversation = useTrackJoinConversation()
  const trackSendMessage = useTrackSendMessage()
  const chatChannelConfig = useChatChannelConfig()
  const showWelcomeMessage = useShowWelcomeMessage()
  const { appointment } = useAppointment()

  return useMemo(() => {
    if (!channel) {
      return {}
    }

    function sendMessage(
      channel: VisitorChannel,
      message: one_to_one.shared.Message,
      isConversationAction: boolean = false,
    ) {
      channel
        .push('send_message', { message })
        .receive('ok', (payload) => {
          if (payload.message) {
            if (!isConversationAction) {
              trackSendMessage({
                messageId: payload.message.id,
                content: payload.message.text_message?.content,
              })
            }

            replaceMessage(payload.message, 'visitor')
          }
        })
        .receive('error', (payload) => {
          logError(new Error(payload.reason))
        })
    }

    const {
      sendUIStateTransitionEvent,
      addChatMessages,
      queueMessage,
      updateMessage,
      replaceMessage,
    } = actionsSelector(storeApi.getState())

    return {
      chatWithAgent: async () => {
        if (chatChannelConfig?.feature_flags?.include_system_messages) {
          let conversation = conversationSelector(storeApi.getState())
          if (!conversation) {
            conversation = await joinConversation(
              channel,
              storeApi,
              trackJoinConversation,
              t,
              chatChannelConfig.config?.classify_conversation_intent,
            )
          }
          channel.push('start_chat', {})
        } else if (shouldSendPrequalificationMessage(storeApi.getState())) {
          setTimeout(() => {
            showWelcomeMessage()
          }, 750)
        }
        sendUIStateTransitionEvent('START_CHAT')
      },
      sendTypingActivity: () => {
        channel.push('is_typing', {})
      },
      sendTextMessage: (text: string, isConversationAction?: boolean) => {
        const conversation = conversationSelector(storeApi.getState())
        const message: one_to_one.shared.Message = {
          client_msg_ref: uuidv4(),
          created_at: new Date().toISOString(),
          conversation_id: conversation?.id,
          text_message: {
            content: text,
            author: 'AUTHOR_VISITOR',
          },
        }
        addChatMessages([message], 'visitor')
        if (conversation) {
          sendMessage(channel, message, isConversationAction)
        } else {
          queueMessage(message)
          joinConversation(
            channel,
            storeApi,
            trackJoinConversation,
            t,
            chatChannelConfig?.config?.classify_conversation_intent,
            appointment,
          )
        }
      },
      sendMediaMessage: (message: one_to_one.shared.Message) => {
        if (
          !message.media_message?.attachments ||
          message.media_message?.attachments.length === 0
        ) {
          return
        }

        const conversation = conversationSelector(storeApi.getState())

        // for media message, we are not supposed to send file_url to backend
        // because that would be a base64 preview that was used
        // to display the media temperarily
        const updatedMessage = {
          ...message,
          conversation_id: conversation?.id,
          media_message: {
            ...message.media_message,
            attachments: [
              {
                ...message.media_message.attachments[0],
                file_url: undefined,
              },
            ],
          },
        }

        if (conversation) {
          sendMessage(channel, updatedMessage)
        } else {
          queueMessage(updatedMessage)
          joinConversation(
            channel,
            storeApi,
            trackJoinConversation,
            t,
            chatChannelConfig?.config?.classify_conversation_intent,
          )
        }
      },
      updateMediaMessage: (
        message: one_to_one.shared.Message,
        uploadStatus: MessageAttachmentUploadStatus,
        progress?: number,
      ) => {
        const previousMessage = messagesSelector(storeApi.getState()).find(
          (m) => m.client_msg_ref === message.client_msg_ref,
        )
        if (!previousMessage) {
          return
        }
        const updatedMessage: Message = {
          ...previousMessage,
          attachment: {
            ...previousMessage.attachment,
            uploadStatus,
            progress,
          },
        }
        updateMessage(updatedMessage)
      },
      sendQueuedMessage: (
        message: one_to_one.shared.Message,
        conversationId: string,
      ) => {
        message.conversation_id = conversationId
        sendMessage(channel, message)
      },
      sendMicrophoneStatus: ({ isMuted }: { isMuted: boolean }) => {
        channel.push('update_microphone_status', {
          is_muted: isMuted,
        })
      },
    }
  }, [
    appointment,
    channel,
    chatChannelConfig?.config?.classify_conversation_intent,
    chatChannelConfig?.feature_flags?.include_system_messages,
    showWelcomeMessage,
    storeApi,
    t,
    trackJoinConversation,
    trackSendMessage,
  ])
}
